import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Modal,
  Typography,
  CircularProgress,
  Divider,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  Close as CloseIconButton,
  CheckCircleOutline as CheckIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import useStyles from './styles';
import { formatCcy } from '../../../utils/helpers';

type ValidatePaymentModalProps = {
  open: boolean;
  onClose: () => void;
  onValidate: (paymentDocumentId: string) => Promise<void>;
  paymentData: {
    paymentDocumentId: string;
    amount: number;
    imageUrl: string;
  } | null;
};

function ValidatePaymentModal({
  open,
  onClose,
  onValidate,
  paymentData,
}: ValidatePaymentModalProps) {
  const classes = useStyles();
  const [imageLoading, setImageLoading] = useState(true);
  const [validating, setValidating] = useState(false);

  const isPdf = paymentData?.imageUrl
    ? paymentData.imageUrl.toLowerCase().includes('.pdf')
      || paymentData.imageUrl.toLowerCase().includes('application/pdf')
    : false;

  const handleValidate = async () => {
    if (!paymentData) return;
    try {
      setValidating(true);
      await onValidate(paymentData.paymentDocumentId);
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setValidating(false);
    }
  };

  useEffect(() => {
    if (open && paymentData?.imageUrl) {
      setImageLoading(true);
    }
  }, [open, paymentData?.imageUrl]);

  if (!paymentData) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={classes.modalBackdrop}
    >
      <Card className={classes.card}>
        <CardContent className={classes.content}>
          <Box className={classes.header}>
            <Typography className={classes.title}>
              Validar pago
            </Typography>
            <IconButton onClick={onClose} className={classes.closeButton}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography className={classes.text}>
            ¿Estás seguro que el comprobante valida correctamente que el pago fue hecho?
          </Typography>

          <Divider />

          <Typography className={classes.text}>
            Monto a pagar: {formatCcy(paymentData.amount, '.')}
          </Typography>

          <Box className={classes.mainSection}>
            <Box className={classes.imageContainer}>
              {paymentData.imageUrl ? (
                <>
                  {isPdf ? (
                    <Box className={classes.pdfContainer}>
                      <iframe
                        src={paymentData.imageUrl}
                        title="Comprobante de pago (PDF)"
                        className={classes.pdfFrame}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<OpenInNewIcon />}
                        href={paymentData.imageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.pdfOpenButton}
                      >
                        Abrir PDF en nueva pestaña
                      </Button>
                    </Box>
                  ) : (
                    <>
                      {imageLoading && (
                        <Box className={classes.imageLoading}>
                          <CircularProgress />
                        </Box>
                      )}
                      <img
                        src={paymentData.imageUrl}
                        alt="Comprobante de pago"
                        className={classes.image}
                        onLoad={() => setImageLoading(false)}
                        onError={() => {
                          setImageLoading(false);
                        }}
                        style={{ display: imageLoading ? 'none' : 'block' }}
                      />
                    </>
                  )}
                </>
              ) : (
                <Typography className={classes.noImageMessage}>
                  No hay comprobante disponible
                </Typography>
              )}
            </Box>
          </Box>

          <Box className={classes.buttonsContainer}>
            <Button
              variant="contained"
              color="error"
              startIcon={<CloseIconButton />}
              onClick={onClose}
              className={classes.cancelButton}
              disabled={validating}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckIcon />}
              onClick={handleValidate}
              className={classes.validateButton}
              disabled={validating}
            >
              {validating ? 'Validando...' : 'Validar pago'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Modal>
  );
}

export default ValidatePaymentModal;
